import { debounce, noop } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import ReactQuill, { QuillOptionsStatic } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './style.scss';

const TOOLBAR_CONTAINER = [
  [{ header: [1, 2, false] }],
  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'image'],
];

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'indent',
  'link',
  'image',
];

interface QuillRange {
  index: number;
  length: number;
}

interface RichTextEditorProps {
  name?: string;
  value?: string;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onImageUpload?: (file: File) => Promise<string>; // l'insertion passe par ce upload au lieu de passé en base64
  onImageUploadError?: (error: unknown) => void;
}

const Editor = ({
  readOnly = false,
  onChange = noop,
  value = '',
  name,
  onImageUpload,
  onImageUploadError = noop,
}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const onImageUploadRef = useRef(onImageUpload);
  const onImageUploadErrorRef = useRef(onImageUploadError);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  useEffect(() => {
    onImageUploadErrorRef.current = onImageUploadError;
  }, [onImageUploadError]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleImageFile = (file: File, range: QuillRange) => {
    const upload = onImageUploadRef.current;
    if (!upload) return;

    upload(file)
      .then((url) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        quill.insertEmbed(range.index, 'image', url, 'user');
        quill.setSelection(range.index + 1, 0, 'user');
        onChangeRef.current(quill.root.innerHTML);
      })
      .catch((error) => onImageUploadErrorRef.current?.(error));
  };
  const handleImageFileRef = useRef(handleImageFile);

  useEffect(() => {
    handleImageFileRef.current = handleImageFile;
  });
  const hasImageUpload = Boolean(onImageUpload);

  const modules: QuillOptionsStatic['modules'] = useMemo(
    () => ({
      toolbar: {
        container: TOOLBAR_CONTAINER,
        handlers: hasImageUpload
          ? {
              image: () => {
                const quill = quillRef.current?.getEditor();
                if (!quill) return;
                const range = quill.getSelection(true) ?? {
                  index: quill.getLength(),
                  length: 0,
                };

                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.style.display = 'none';
                document.body.appendChild(input);
                input.onchange = () => {
                  const file = input.files?.[0];
                  if (file) handleImageFileRef.current(file, range);
                  document.body.removeChild(input);
                };
                input.click();
              },
            }
          : undefined,
      },
    }),
    [hasImageUpload],
  );

  useEffect(() => {
    if (!onImageUpload) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const root = quill.root;

    const firstImageFile = (transfer: DataTransfer | null): File | null =>
      transfer
        ? (Array.from(transfer.files).find((f) =>
            f.type.startsWith('image/'),
          ) ?? null)
        : null;

    const handlePaste = (event: ClipboardEvent) => {
      const file = firstImageFile(event.clipboardData);
      if (!file) return; // pas d'image collée alors Quill gère le texte/HTML normalement
      event.preventDefault();
      event.stopPropagation();
      const range = quill.getSelection(true) ?? {
        index: quill.getLength(),
        length: 0,
      };
      handleImageFileRef.current(file, range);
    };

    const handleDrop = (event: DragEvent) => {
      const file = firstImageFile(event.dataTransfer);
      if (!file) return;
      event.preventDefault();
      event.stopPropagation();
      const range = quill.getSelection(true) ?? {
        index: quill.getLength(),
        length: 0,
      };
      handleImageFileRef.current(file, range);
    };

    root.addEventListener('paste', handlePaste, true);
    root.addEventListener('drop', handleDrop, true);

    return () => {
      root.removeEventListener('paste', handlePaste, true);
      root.removeEventListener('drop', handleDrop, true);
    };
  }, [onImageUpload]);

  const debouncedOnChange = useMemo(() => debounce(onChange, 500), [onChange]);

  return (
    <div className='rich-text-container'>
      <ReactQuill
        ref={quillRef}
        data-cy={name}
        className={readOnly ? 'quill-disabled' : undefined}
        style={{
          overflowY: 'auto',
          alignItems: 'center',
          marginBottom: 10,
          opacity: readOnly ? 0.7 : 1,
        }}
        modules={modules}
        formats={formats}
        value={value}
        readOnly={readOnly}
        onChange={debouncedOnChange}
        bounds='.rich-text-container'
      />
    </div>
  );
};

export default Editor;
